import { ShieldCheck, Download, Code, Plug } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// Documentation display — shows production API endpoints
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || "https://nextxus.org"}/api/hub`;

export default function GrokPlugin() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 px-4 py-8 max-w-4xl mx-auto" data-testid="grok-plugin">
      <div className="text-center mb-8">
        <Plug className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-slate-100 mb-1">NextXus Emergent Truth</h1>
        <p className="text-sm text-slate-500">Plugin for Grok AI — Ring of Six Verification + Business Automation</p>
        <p className="text-xs text-slate-600 mt-1">v0.2.1 — MIT License — Roger Keyserling</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Card className="border border-slate-800/40 bg-slate-900/30">
          <CardContent className="p-5">
            <ShieldCheck className="w-6 h-6 text-cyan-400 mb-3" />
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Ring of Six Verification</h3>
            <p className="text-xs text-slate-400">Full multi-perspective analysis: MIND, HEART, HANDS, LEGS, EYE, AGENT. Reduces hallucinations, surfaces blind spots.</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-800/40 bg-slate-900/30">
          <CardContent className="p-5">
            <Code className="w-6 h-6 text-amber-400 mb-3" />
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Hallucination Check</h3>
            <p className="text-xs text-slate-400">Fast mode: MIND + LEGS only. Quick verification of factual claims without full council overhead.</p>
          </CardContent>
        </Card>
      </div>

      {/* Installation */}
      <Card className="border border-slate-800/40 bg-slate-900/30 mb-6">
        <CardContent className="p-5">
          <h2 className="text-base font-semibold text-slate-200 mb-3">Installation</h2>
          <p className="text-xs text-slate-400 mb-3">Say this in any Grok session:</p>
          <div className="bg-slate-800/60 rounded-lg p-4 font-mono text-sm text-cyan-300 mb-4">
            "Activate NextXus Emergent Truth"
          </div>
          <h3 className="text-sm font-medium text-slate-300 mb-2">Triggers</h3>
          <div className="flex flex-wrap gap-2">
            {['truth verify', 'Ring of Six', 'hallucination check', 'automation analyzer', 'enable immutable logging'].map(t => (
              <span key={t} className="text-xs bg-slate-800 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/40">{t}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API Endpoints */}
      <Card className="border border-slate-800/40 bg-slate-900/30 mb-6">
        <CardContent className="p-5">
          <h2 className="text-base font-semibold text-slate-200 mb-3">Public API</h2>
          <div className="space-y-3 font-mono text-xs">
            <div>
              <span className="text-emerald-400">POST</span> <span className="text-slate-300">{API_BASE}/ring-of-six</span>
              <p className="text-slate-500 font-sans mt-1">Full Ring of Six verification. Body: {`{"question": "..."}`}</p>
            </div>
            <div>
              <span className="text-amber-400">POST</span> <span className="text-slate-300">{API_BASE}/hallucination-check</span>
              <p className="text-slate-500 font-sans mt-1">Fast MIND+LEGS check. Body: {`{"claim": "..."}`}</p>
            </div>
            <div>
              <span className="text-cyan-400">POST</span> <span className="text-slate-300">{API_BASE}/truth-gate</span>
              <p className="text-slate-500 font-sans mt-1">Core Truth Gate proxy. Body: {`{"content": "..."}`}</p>
            </div>
            <div>
              <span className="text-slate-400">GET</span> <span className="text-slate-300">{API_BASE}/grok-plugin/manifest</span>
              <p className="text-slate-500 font-sans mt-1">Plugin manifest (OpenAPI-compatible)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attribution */}
      <div className="text-center text-xs text-slate-600">
        <p>HumanCodex NextXus Federation — Roger Keyserling</p>
        <p className="mt-1">MIT License — Free to use, modify, and distribute</p>
      </div>
    </div>
  );
}
